import React, { useMemo, type ReactNode } from 'react';

import { useHubTheming } from '../../controllers/hub-theming-context';
import { useIsHubTypographyEnabled } from '../../controllers/use-is-hub-typography-enabled';
import {
	buildGoogleFontsUrl,
	FONT_REGISTRY,
	HUB_HEADING_TOKEN_MAP,
	HUB_PARAGRAPH_TOKEN_MAP,
} from '../../utils/hub-typography';

/**
 * Force the picked typography onto every descendant of HubTypographyScope,
 * including admin-published content rendered inside the editor (info
 * panels, decision lists, headings, paragraphs, etc.).
 *
 * Why this rule is needed — observed in DevTools:
 *   - An emotion-styled Hub layout box (e.g. `.fEBamI`) renders ABOVE
 *     HubTypographyScope and declares `font: var(--ds-font-body)` on
 *     itself. At that ancestor our scope isn't in scope yet, so
 *     `var(--ds-font-body)` resolves to AK's default and the `font`
 *     shorthand sets `font-family: <Atlassian Sans / ui-sans-serif, ...>`
 *     directly. That declared `font-family` then cascades down through
 *     HubTypographyScope, through the editor wrapper, and into every
 *     paragraph and heading the admin inserts.
 *   - Overriding CSS variables alone doesn't break the chain because
 *     `font-family` is already declared (inherited) at HubTypographyScope's
 *     ancestor and nothing inside the editor `var()`-references our handles
 *     for font-family itself.
 *
 * Fix: declare `font-family` directly at HubTypographyScope's wrapper
 * (paragraph stack) so descendants inherit the picked family, and
 * override to the heading stack for any heading element inside the scope.
 *
 * The editor `--editor-font-ugc-token-*` redeclarations stay as a
 * defensive hook for any editor surface that does consume them via
 * `font: var(--editor-font-ugc-token-body)`. Late-bound `var()`
 * substitution lets HubTypographyResetScope reset them back to
 * Atlassian Sans for admin-only popovers without touching this rule.
 */
const TYPOGRAPHY_CSS = `
[data-hub-typography-scope] :where(h1, h2, h3, h4, h5, h6) {
	/*
	 * !important is required to beat the renderer's heading font.
	 * @atlaskit/renderer renders view-mode headings with
	 * \`font: var(--ak-renderer-editor-font-heading-*)\` — a \`font\`
	 * shorthand at specificity (0,2,0) whose family segment is the
	 * hardcoded \`ui-sans-serif, -apple-system, ...\` stack. We can't
	 * override that token (the renderer sets it inline on its own
	 * dynamic-class container, which shadows any inherited override), so
	 * we override the family longhand with !important. It wins over the
	 * non-important shorthand's family while leaving the renderer's
	 * size / weight / line-height intact. The same rule covers editor
	 * (.ProseMirror) and AK Heading components, which is why it's applied
	 * scope-wide rather than only to the renderer.
	 */
	font-family: var(--hub-heading-font-family) !important;
}

[data-hub-typography-scope] .ProseMirror {
	--editor-font-ugc-token-heading-h1: normal 500 1.71429em / 1.16667 var(--hub-heading-font-family);
	--editor-font-ugc-token-heading-h2: normal 500 1.42857em / 1.2 var(--hub-heading-font-family);
	--editor-font-ugc-token-heading-h3: normal 600 1.14286em / 1.25 var(--hub-heading-font-family);
	--editor-font-ugc-token-heading-h4: normal 600 1em / 1.14286 var(--hub-heading-font-family);
	--editor-font-ugc-token-heading-h5: normal 600 0.857143em / 1.33333 var(--hub-heading-font-family);
	--editor-font-ugc-token-heading-h6: normal 700 0.785714em / 1.45455 var(--hub-heading-font-family);
	--editor-font-ugc-token-body: normal 400 1em / 1.714 var(--hub-paragraph-font-family);
}

[data-hub-typography-scope] .ak-renderer-document {
	/*
	 * View-mode (renderer) UGC typography — the mirror of the .ProseMirror block
	 * above. @atlaskit/renderer renders native ADF content (paragraphs, lists,
	 * panels, expand, blockquotes, dates, layout columns, table cells, headings…)
	 * by reading its own --ak-renderer-editor-font-* "font" shorthand tokens,
	 * which bake in the default Atlassian Sans family. We redefine those tokens
	 * with the picked hub family — the values are copied verbatim from
	 * @atlaskit/editor-common's editorUGCTokensRefreshed and ONLY the trailing
	 * family segment is swapped — so the renderer's size / weight / line-height
	 * are preserved exactly and only the family changes. !important is needed to
	 * beat the renderer setting these tokens inline on its own container.
	 *
	 * Why remap tokens instead of forcing font-family on every element: this
	 * touches ONLY the renderer's private tokens — never font-family directly and
	 * never the shared --ds-font-* tokens. So bespoke extensions (spotlight,
	 * cards, carousel, …), which style their titles/body with --ds-font-* and are
	 * remapped once on the wrapper (HUB_HEADING_TOKEN_MAP / HUB_PARAGRAPH_TOKEN_MAP),
	 * are untouched and keep the correct heading vs body font in both edit and
	 * view mode. Code (@atlaskit/code, its own monospace token) and
	 * HubTypographyResetScope subtrees don't read these tokens either, so they are
	 * left alone automatically — no per-element exclusions required.
	 *
	 * Both modes now theme font *tokens* rather than overriding elements, so a new
	 * ADF node type is covered in both modes with no per-element work.
	 */
	--ak-renderer-editor-font-heading-h1: normal 600 1.71429em / 1.16667 var(--hub-heading-font-family) !important;
	--ak-renderer-editor-font-heading-h2: normal 600 1.42857em / 1.2 var(--hub-heading-font-family) !important;
	--ak-renderer-editor-font-heading-h3: normal 600 1.14286em / 1.25 var(--hub-heading-font-family) !important;
	--ak-renderer-editor-font-heading-h4: normal 600 1em / 1.14286 var(--hub-heading-font-family) !important;
	--ak-renderer-editor-font-heading-h5: normal 600 0.857143em / 1.33333 var(--hub-heading-font-family) !important;
	--ak-renderer-editor-font-heading-h6: normal 600 0.785714em / 1.45455 var(--hub-heading-font-family) !important;
	--ak-renderer-editor-font-normal-text: normal 400 1em / 1.714 var(--hub-paragraph-font-family) !important;
	--ak-renderer-editor-font-small-text: normal 400 0.875em / 1.714 var(--hub-paragraph-font-family) !important;
}
`;

/**
 * Scopes Hub typography overrides to its subtree.
 *
 * When the gate is ON and the admin has picked a typography, this
 * component renders:
 *
 *   1. A `<link rel="stylesheet" href="...">` pointing at the Google Fonts
 *      CSS API for any non-default fonts. React 18 auto-hoists it to
 *      `<head>`. Atlassian Sans is skipped (already loaded by the AK shell).
 *   2. An inline `<style>` that overrides the editor's UGC font tokens
 *      so admin-published content (info panel, decision list, etc.)
 *      renders with the admin's picked typography in edit mode too.
 *   3. A `<div>` wrapper carrying CSS-var overrides for the family
 *      handles plus every composite typography token. `display: contents`
 *      keeps the wrapper invisible to ancestor flex/grid layouts.
 *
 * Everything is inline JSX — no `useEffect`, no DOM mutation, no
 * `document.head.appendChild`. Fully SSR-safe.
 *
 * Gate-off / typography-undefined / cross-host: renders `children`
 * verbatim with no wrapper.
 */
export const HubTypographyScope = ({ children }: { children: ReactNode }): JSX.Element => {
	const enabled = useIsHubTypographyEnabled();
	const theming = useHubTheming();
	const typography = theming?.typography;

	const googleFontsUrl = useMemo(
		() => (typography ? buildGoogleFontsUrl([typography.heading, typography.body]) : ''),
		[typography],
	);

	const wrapperStyle = useMemo<React.CSSProperties>(() => {
		if (!typography) return {};
		// FONT_REGISTRY values are intentionally hard-coded font stacks — this
		// component's entire purpose is to override AK's typography tokens with
		// admin-picked families, so AK design-system tokens cannot be used here.
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		const heading = FONT_REGISTRY[typography.heading].family;
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		const body = FONT_REGISTRY[typography.body].family;
		return {
			display: 'contents',
			// Points at our own --hub-paragraph-font-family handle so descendants
			// inherit the picked family. AK tokens cannot be used here — this IS
			// the override mechanism.
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontFamily: 'var(--hub-paragraph-font-family)',
			'--hub-heading-font-family': heading,
			'--hub-paragraph-font-family': body,
			...HUB_HEADING_TOKEN_MAP,
			...HUB_PARAGRAPH_TOKEN_MAP,
		} as unknown as React.CSSProperties;
	}, [typography]);

	if (!enabled || !typography) {
		return <>{children}</>;
	}

	return (
		<>
			{googleFontsUrl && <link rel="stylesheet" href={googleFontsUrl} />}
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles */}
			<style>{TYPOGRAPHY_CSS}</style>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
			<div data-hub-typography-scope="" style={wrapperStyle}>
				{children}
			</div>
		</>
	);
};
